<script setup lang="ts">
import { Version } from '@/@types/order'
import { Stepper, StepperDescription, StepperItem, StepperSeparator, StepperTitle, StepperTrigger } from '@/components/ui/stepper'
import { formatDateFull, formatDateFullWithHours } from '@/helpers/format-date'

defineProps<{
    type: 'order' | 'mailing' | 'item'
    version: Version[]
}>()
</script>

<template>
    <div class="relative mt-8 grid">
        <Stepper v-if="version.length" orientation="vertical" class="flex w-full flex-col justify-start gap-10 overflow-hidden">
            <StepperItem v-for="(step, index) in version" :key="step.item_id" class="relative flex w-full items-start gap-6" :step="index + 1">
                <StepperSeparator
                    class="absolute top-[35px] left-[15px] block h-[100%] w-0.5 shrink-0 rounded-full bg-gray-200 group-data-[disabled]:bg-gray-200 group-data-[disabled]:opacity-100"
                />

                <StepperTrigger as-child class="flex size-8 items-center justify-center rounded-full">
                    <div class="bg-gray-100">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM12 7C12 8.10457 11.1046 9 10 9C8.89543 9 8 8.10457 8 7C8 5.89543 8.89543 5 10 5C11.1046 5 12 5.89543 12 7ZM9.99993 11C7.98239 11 6.24394 12.195 5.45374 13.9157C6.55403 15.192 8.18265 16 9.99998 16C11.8173 16 13.4459 15.1921 14.5462 13.9158C13.756 12.195 12.0175 11 9.99993 11Z"
                                fill="#6B7280"
                            />
                        </svg>
                    </div>
                </StepperTrigger>

                <div class="flex w-full flex-col justify-between gap-1 md:flex-row">
                    <div class="flex flex-col gap-1">
                        <StepperTitle class="font-semibold whitespace-break-spaces text-slate-800 transition lg:text-base">
                            {{ $t(step.item_type) }} {{ $t(step.event) }}
                        </StepperTitle>

                        <div class="mb-2 text-slate-600" v-if="step.whodunnit">
                            <strong>Atualizado por:</strong> {{ step.whodunnit.name }}
                            <span v-show="step.whodunnit.email"> - {{ step.whodunnit.email }}</span>
                        </div>

                        <StepperDescription>
                            <div v-if="type === 'mailing' && step.event !== 'create'" class="text-xs text-gray-600 transition lg:text-sm">
                                <strong>E-mail:</strong> {{ step.object_data.email }}
                            </div>
                            <div
                                v-for="key in Object.entries(step.object_changes_data)"
                                class="text-xs text-gray-600 transition lg:text-sm"
                                :key="key[0]"
                            >
                                <template
                                    v-if="
                                        !['updated_at', 'profile', 'id', 'user_id', 'item_id', 'total', 'total_cents', 'handler_user_id'].includes(
                                            key[0],
                                        )
                                    "
                                >
                                    <div>
                                        <strong>{{ $t(`paper_trail_${type}.${key[0]}`) }}: </strong>

                                        <template v-if="key[0] === 'status'">
                                            <span v-if="String(key[1][0]) && key[1][0] !== null">
                                                <span class="line-through">
                                                    {{ $t(`paper_trail_${type}.statuses.${key[1][0]}`) }}
                                                </span>
                                                ->
                                            </span>

                                            {{ $t(`paper_trail_${type}.statuses.${key[1][1]}`) }}
                                        </template>
                                        <template v-else-if="key[0] === 'cancel_reason'">
                                            <span class="line-through">
                                                {{ $t(`paper_trail_${type}.motivations.${key[1][1]}`) }}
                                            </span>
                                        </template>
                                        <template v-else-if="key[0] === 'group'">
                                            <span v-if="String(key[1][0]) && key[1][0] !== null">
                                                <span class="line-through">{{ $t(`paper_trail_${type}.groups.${key[1][0]}`) }}</span> -></span
                                            >

                                            {{ $t(`paper_trail_${type}.groups.${key[1][1]}`) }}
                                        </template>
                                        <template v-else-if="key[0] === 'payment_type'">
                                            <span v-if="String(key[1][0]) && key[1][0] !== null">
                                                <span class="line-through">{{ $t(`paper_trail_${type}.payment_types.${key[1][0]}`) }}</span> -></span
                                            >

                                            {{ $t(`paper_trail_${type}.payment_types.${key[1][1]}`) }}
                                        </template>
                                        <template v-else-if="key[0] === 'profile'">
                                            <span v-if="String(key[1][0]) && key[1][0] !== null">
                                                <span class="line-through">
                                                    {{ $t(`paper_trail_${type}.profiles.${key[1][0]}`) }}
                                                </span>
                                                -></span
                                            >

                                            {{ $t(`paper_trail_${type}.profiles.${key[1][1]}`) }}
                                        </template>
                                        <template v-else-if="key[0] === 'created_at' || key[0] === 'company_billing_due_date'">
                                            <span v-if="key[1][0]"> {{ formatDateFull(`${key[1][0]}`) }} -></span>
                                            {{ formatDateFull(`${key[1][1]}`) }}
                                        </template>

                                        <template v-else>
                                            <span v-if="type === 'item'">{{ $t(`${step.object_data.ticket_category}`) }}</span>
                                            <span v-else>
                                                <span v-if="key[1][0]">
                                                    <span class="line-through">
                                                        {{ $t(`${key[1][0]}`) }}
                                                    </span>
                                                    ->
                                                </span>
                                                {{ key[1][1] }}
                                            </span>
                                        </template>
                                    </div>
                                </template>
                            </div>
                        </StepperDescription>
                    </div>

                    <time class="font-sans text-xs text-gray-600 lg:text-sm">{{ formatDateFullWithHours(step.created_at) }}</time>
                </div>
            </StepperItem>
        </Stepper>

        <p v-else class="text-center">Nenhum registro encontrado no histórico</p>
    </div>
</template>
